import {
	captureErrorToSentry,
	serializeErrorForSentry,
} from "@helpers/sentry-logger";
import { useAppStore } from "@hooks/useAppStore";
import { fetchSessionAssessmentStatus } from "../api";
import type { SessionAssessmentItemStatus } from "../api/session-assessment-status";
import useAIFeedbackStore from "../hooks/useAIFeedbackStore";
import useSessionStore from "../hooks/useSessionStore";
import useSummativeAssessmentStore from "../hooks/useSummativeAssessmentStore";
import {
	getSummativeAiItemIds,
	getSummativeFeedbackItemIds,
} from "./summative-ai-gate";
import {
	type RunAssessmentCtx,
	runAssessment,
} from "./summative-assessment-runner";
import { runFeedback } from "./summative-feedback-runner";

export type ReconcileSummativeAssessmentsCtx = RunAssessmentCtx;

const reconcileItem = (
	item: SessionAssessmentItemStatus,
	isFeedbackItem: boolean,
	ctx: ReconcileSummativeAssessmentsCtx,
): void => {
	const answer = item.aiResponse?.answer;
	const aiFeedbackStore = useAIFeedbackStore.getState();
	const runStore = useSummativeAssessmentStore.getState();

	if (item.status === "COMPLETE" && item.aiResponse) {
		aiFeedbackStore.updateExplanation(item.itemId, item.aiResponse.feedback);
		aiFeedbackStore.updateSubskillAssessments(
			item.itemId,
			item.aiResponse.subskillAssessments,
		);
		if (answer != null) {
			aiFeedbackStore.updateAnswer(item.itemId, answer);
			aiFeedbackStore.setUserResponse(item.itemId, answer);
		}
		aiFeedbackStore.updateStatus(item.itemId, "COMPLETE");

		const seq = runStore.beginRun(
			item.itemId,
			isFeedbackItem ? answer : undefined,
		);
		runStore.markAssessed(item.itemId, seq);
		return;
	}

	const shouldRefire =
		item.status === "PENDING" || (item.status === "MISSING" && Boolean(answer));
	if (!shouldRefire) {
		return;
	}

	if (answer != null) {
		aiFeedbackStore.setUserResponse(item.itemId, answer);
		aiFeedbackStore.updateAnswer(item.itemId, answer);
	}

	if (isFeedbackItem) {
		// No in-session stash can exist yet this early in a fresh resume — mark
		// stranded upfront so the submit gate never waits on a follow-up post
		// that has nothing safe to persist. runFeedback un-strands it if the
		// user later revisits and re-answers the item live.
		runStore.markFeedbackStranded(item.itemId);
		runStore.markResponseChanged(item.itemId);
		runFeedback(item.itemId, ctx).catch(() => undefined);
		return;
	}

	runStore.markResponseChanged(item.itemId);
	runAssessment(item.itemId, ctx).catch(() => undefined);
};

export const reconcileSummativeAssessments = async (
	ctx: ReconcileSummativeAssessmentsCtx,
): Promise<void> => {
	const sessionState = useSessionStore.getState();
	const testDriver = useAppStore.getState().sessionView?.testDriver;

	const assessorItemIds = getSummativeAiItemIds(
		sessionState.sessionItems,
		testDriver,
		sessionState.isSummativeTest,
	);
	const feedbackItemIds = getSummativeFeedbackItemIds(
		sessionState.sessionItems,
		testDriver,
		sessionState.isSummativeTest,
	);
	const aiItemIds = [...assessorItemIds, ...feedbackItemIds];

	if (aiItemIds.length === 0) {
		return;
	}

	let response: Awaited<ReturnType<typeof fetchSessionAssessmentStatus>>;
	try {
		response = await fetchSessionAssessmentStatus(sessionState.id, aiItemIds);
	} catch (error) {
		captureErrorToSentry(
			new Error("Error reconciling summative assessment state on resume"),
			{ error: serializeErrorForSentry(error), sessionId: sessionState.id },
		);
		return;
	}

	const feedbackIdSet = new Set(feedbackItemIds);
	for (const item of response.items) {
		reconcileItem(item, feedbackIdSet.has(item.itemId), ctx);
	}
};
